if (!customElements.get('gpused-sticky-atc')) {
  customElements.define(
    'gpused-sticky-atc',
    class GPUsedStickyAtc extends HTMLElement {
      connectedCallback() {
        if (this.initialised) return;
        this.initialised = true;
        this.button = this.querySelector('[data-gpused-sticky-add]');
        this.currentPrice = this.querySelector('[data-gpused-sticky-current]');
        this.compareAtPrice = this.querySelector('[data-gpused-sticky-was]');
        this.variantData = this.readVariantData();
        this.triggerPassed = false;

        this.handleViewportChange = this.handleViewportChange.bind(this);
        this.handleProductChange = this.handleProductChange.bind(this);
        this.handleSectionLoad = this.handleSectionLoad.bind(this);
        this.scheduleReconcile = this.scheduleReconcile.bind(this);

        window.addEventListener('resize', this.handleViewportChange, { passive: true });
        window.addEventListener('pageshow', this.handleViewportChange);
        document.addEventListener('shopify:section:load', this.handleSectionLoad);

        this.pageObserver = new MutationObserver(this.scheduleReconcile);
        this.pageObserver.observe(document.body, {
          attributes: true,
          attributeFilter: ['aria-hidden', 'class', 'disabled', 'open'],
          childList: true,
          subtree: true,
        });

        this.bindNativeButton();
        this.reconcile();
      }

      disconnectedCallback() {
        window.removeEventListener('resize', this.handleViewportChange);
        window.removeEventListener('pageshow', this.handleViewportChange);
        document.removeEventListener('shopify:section:load', this.handleSectionLoad);
        window.removeEventListener('scroll', this.scheduleReconcile);
        this.nativeForm?.removeEventListener('change', this.handleProductChange);
        this.intersectionObserver?.disconnect();
        this.pageObserver?.disconnect();
        if (this.frame) cancelAnimationFrame(this.frame);
        document.documentElement.classList.remove('gpused-sticky-atc-visible');
      }

      readVariantData() {
        try {
          return JSON.parse(this.querySelector('[data-gpused-sticky-variants]')?.textContent || '{}');
        } catch (error) {
          return {};
        }
      }

      findNativeButton() {
        return document.querySelector('#MainContent product-form button[name="add"]');
      }

      bindNativeButton() {
        const nextButton = this.findNativeButton();
        if (nextButton === this.nativeButton) return;

        this.nativeForm?.removeEventListener('change', this.handleProductChange);
        this.intersectionObserver?.disconnect();
        this.nativeButton = nextButton;
        this.nativeForm = nextButton?.closest('form') || null;
        this.nativeForm?.addEventListener('change', this.handleProductChange);
        this.triggerPassed = false;

        const nativeFormId = this.nativeForm?.getAttribute('id');
        if (nativeFormId) {
          this.button?.setAttribute('form', nativeFormId);
        } else {
          this.button?.removeAttribute('form');
        }

        if (!this.nativeButton) {
          this.hide();
          return;
        }

        window.addEventListener('scroll', this.scheduleReconcile, { passive: true });

        if ('IntersectionObserver' in window) {
          this.intersectionObserver = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
              const viewportTop = window.visualViewport?.offsetTop || 0;
              const viewportHeight = window.visualViewport?.height || window.innerHeight;
              const viewportBottom = viewportTop + viewportHeight;
              const productInfo = this.nativeButton.closest('product-info');
              const productInfoTop = productInfo?.getBoundingClientRect().top ?? viewportBottom;
              const actionOutsideViewport =
                !entry.isIntersecting &&
                (entry.boundingClientRect.bottom <= viewportTop ||
                  entry.boundingClientRect.top >= viewportBottom);
              this.triggerPassed =
                actionOutsideViewport && productInfoTop < viewportBottom - 80;
            });
            this.reconcile();
          });
          this.intersectionObserver.observe(this.nativeButton);
        }

        this.syncVariant();
      }

      handleSectionLoad() {
        this.bindNativeButton();
        this.reconcile();
      }

      handleViewportChange() {
        this.bindNativeButton();
        this.reconcile();
      }

      handleProductChange() {
        window.setTimeout(() => {
          this.syncVariant();
          this.reconcile();
        }, 0);
      }

      scheduleReconcile() {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.bindNativeButton();
          this.syncVariant();
          this.reconcile();
        });
      }

      getVariantId() {
        return String(this.nativeForm?.querySelector('[name="id"]')?.value || '');
      }

      syncVariant() {
        const variant = this.variantData[this.getVariantId()];
        this.currentVariant = variant || null;

        if (!variant) {
          if (this.currentPrice.textContent !== '') this.currentPrice.textContent = '';
          if (!this.compareAtPrice.hidden) this.compareAtPrice.hidden = true;
          return;
        }

        if (this.currentPrice.textContent !== variant.price) this.currentPrice.textContent = variant.price;
        if (this.compareAtPrice.textContent !== (variant.compareAt || '')) {
          this.compareAtPrice.textContent = variant.compareAt || '';
        }
        if (this.compareAtPrice.hidden === Boolean(variant.compareAt)) {
          this.compareAtPrice.hidden = !variant.compareAt;
        }
      }

      nativeActionAvailable() {
        return Boolean(
          this.nativeButton &&
            this.currentVariant?.available &&
            !this.nativeButton.disabled &&
            this.nativeButton.getAttribute('aria-disabled') !== 'true' &&
            !this.nativeButton.classList.contains('loading'),
        );
      }

      hasBlockingSurface() {
        const selector = [
          '#gpused-assistant.gpused-assistant--open',
          'cart-drawer.active',
          'cart-notification .cart-notification.active',
          'pickup-availability-drawer[open]',
          'modal-dialog[open]',
          'header-drawer > details[open]',
          'details-modal details[open]',
          '[role="dialog"][aria-modal="true"]',
          '[role="alertdialog"]',
          '#shopify-pc__banner',
          '.shopify-pc__banner__dialog',
          '[data-shopify-privacy-banner]',
        ].join(',');

        return Array.from(document.querySelectorAll(selector)).some((element) => {
          if (element.hidden || element.getAttribute('aria-hidden') === 'true') return false;
          const style = window.getComputedStyle(element);
          const rect = element.getBoundingClientRect();
          const intersectsViewport =
            rect.right > 0 &&
            rect.bottom > 0 &&
            rect.left < window.innerWidth &&
            rect.top < window.innerHeight;
          return (
            style.display !== 'none' &&
            style.visibility !== 'hidden' &&
            rect.width > 0 &&
            rect.height > 0 &&
            intersectsViewport
          );
        });
      }

      reconcile() {
        if (!this.nativeButton) {
          this.hide();
          return;
        }

        const viewportTop = window.visualViewport?.offsetTop || 0;
        const viewportHeight = window.visualViewport?.height || window.innerHeight;
        const viewportBottom = viewportTop + viewportHeight;
        const nativeButtonRect = this.nativeButton.getBoundingClientRect();
        const productInfo = this.nativeButton.closest('product-info');
        const productInfoTop = productInfo?.getBoundingClientRect().top ?? viewportBottom;
        const actionOutsideViewport =
          nativeButtonRect.bottom <= viewportTop || nativeButtonRect.top >= viewportBottom;
        this.triggerPassed =
          actionOutsideViewport && productInfoTop < viewportBottom - 80;

        const shouldShow =
          window.matchMedia('(max-width: 749px)').matches &&
          this.triggerPassed &&
          this.nativeActionAvailable() &&
          !this.hasBlockingSurface();

        if (shouldShow) this.show();
        else this.hide();
      }

      show() {
        document.documentElement.classList.add('gpused-sticky-atc-visible');
        this.inert = false;
        if (this.getAttribute('aria-hidden') !== 'false') this.setAttribute('aria-hidden', 'false');
        this.button?.removeAttribute('tabindex');
        if (this.button?.getAttribute('aria-disabled') !== 'false') {
          this.button?.setAttribute('aria-disabled', 'false');
        }
      }

      hide() {
        document.documentElement.classList.remove('gpused-sticky-atc-visible');
        this.inert = true;
        if (this.getAttribute('aria-hidden') !== 'true') this.setAttribute('aria-hidden', 'true');
        if (this.button?.getAttribute('tabindex') !== '-1') this.button?.setAttribute('tabindex', '-1');
        if (this.button?.getAttribute('aria-disabled') !== 'true') {
          this.button?.setAttribute('aria-disabled', 'true');
        }
      }

    },
  );
}
